import React, {
    useCallback,
    useEffect,
    useState,
} from 'react';
import {
    NavLink,
    useNavigate,
    useParams,
} from 'react-router-dom';
import {
    Badge,
    Button,
    Card,
    Col,
    Container,
    Form,
    InputGroup,
    OverlayTrigger,
    Row,
    Spinner,
    Tooltip,
} from 'react-bootstrap';
import { Formik } from 'formik';
import { FaRegCopy } from 'react-icons/fa';

import API from '../../../api/axios';
import { useFormat } from '../../../components/FormatContext';

const REDEEM_SCOPE_OPTIONS = [
    {
        value: 'ANY_SHOP',
        label: 'Any shop',
    },
    {
        value: 'SAME_SHOP',
        label: 'Same shop only',
    },
];

const BASE_INITIAL_VALUES = {
    code: '',
    discount: '0.00',
    currency: 'EUR',
    validEmail: '',
    validStartDate: '',
    validEndDate: '',

    redeemScope: 'ANY_SHOP',
    redeemShopId: '',

    isActive: true,

    isUsed: false,
    isExpired: false,
    notifiedAt: null,
    createdAt: null,
    updatedAt: null,
    orderId: null,
    notificationOutboxId: null,
};

const FormSection = ({
    title,
    level = 1,
    children,
    button = null,
}) => {
    const Tag =
        `h${Math.min(
            6,
            level + 3,
        )}`;

    return (
        <section className="mb-4 form-section">
            <Tag
                className={
                    `text-muted ${level === 1
                        ? 'fw-semibold'
                        : 'fw-medium'
                    }`
                }
            >
                {title} {button}
            </Tag>

            {children}
        </section>
    );
};

const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(
            text,
        );
    } catch {
        // ignore
    }
};

const isValidMoney = (value) =>
    /^\d+(\.\d{1,2})?$/.test(
        String(
            value || '',
        ).trim(),
    );

const toDateTimeLocal = (value) => {
    if (!value) {
        return '';
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime(),
        )
    ) {
        return '';
    }

    const pad = (number) =>
        String(number).padStart(
            2,
            '0',
        );

    const year =
        date.getFullYear();

    const month =
        pad(
            date.getMonth() +
            1,
        );

    const day =
        pad(
            date.getDate(),
        );

    const hours =
        pad(
            date.getHours(),
        );

    const minutes =
        pad(
            date.getMinutes(),
        );

    return (
        `${year}-${month}-${day}` +
        `T${hours}:${minutes}`
    );
};

const fromDateTimeLocal = (value) => {
    if (!value) {
        return null;
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime(),
        )
    ) {
        return null;
    }

    return date.toISOString();
};

const normalizeCode = (value) =>
    String(
        value || '',
    ).trim();

const normalizeEmail = (value) =>
    String(
        value || '',
    )
        .trim()
        .toLowerCase();

const normalizeShopId = (value) =>
    String(
        value || '',
    ).trim();

const CreditCodeFormPage = () => {
    const {
        baseFormatters,
    } = useFormat();

    const { id } =
        useParams();

    const navigate =
        useNavigate();

    const isEdit =
        Boolean(id);

    const [loading, setLoading] =
        useState(isEdit);

    const [saving, setSaving] =
        useState(false);

    const [error, setError] =
        useState('');

    const [
        initialValues,
        setInitialValues,
    ] = useState(
        BASE_INITIAL_VALUES,
    );

    const loadCreditCode =
        useCallback(async () => {
            if (!isEdit) {
                return null;
            }

            const { data } =
                await API.get(
                    `/codes/credits/${id}`,
                );

            return data;
        }, [
            id,
            isEdit,
        ]);

    useEffect(() => {
        if (!isEdit) {
            return undefined;
        }

        let mounted = true;

        const load = async () => {
            try {
                setLoading(true);
                setError('');

                const data =
                    await loadCreditCode();

                if (!mounted) {
                    return;
                }

                setInitialValues(
                    (prev) => ({
                        ...prev,

                        code:
                            data?.code ??
                            '',

                        discount:
                            data?.discount !=
                                null
                                ? String(
                                    data.discount,
                                )
                                : prev.discount,

                        currency:
                            (
                                data?.currency ??
                                prev.currency
                            ).toUpperCase(),

                        validEmail:
                            data?.validEmail ??
                            '',

                        validStartDate:
                            toDateTimeLocal(
                                data?.validStartDate,
                            ),

                        validEndDate:
                            toDateTimeLocal(
                                data?.validEndDate,
                            ),

                        redeemScope:
                            data?.redeemScope ??
                            prev.redeemScope,

                        redeemShopId:
                            data?.redeemShopId ??
                            '',

                        isActive:
                            !!data?.isActive,

                        isUsed:
                            !!data?.isUsed,

                        isExpired:
                            !!data?.isExpired,

                        notifiedAt:
                            data?.notifiedAt ??
                            null,

                        createdAt:
                            data?.createdAt ??
                            null,

                        updatedAt:
                            data?.updatedAt ??
                            null,

                        orderId:
                            data?.orderId ??
                            null,

                        notificationOutboxId:
                            data?.notificationOutboxId ??
                            null,
                    }),
                );
            } catch (e) {
                if (mounted) {
                    setError(
                        e?.response?.data
                            ?.message ||
                        e?.message ||
                        'Failed to load credit code',
                    );
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            mounted = false;
        };
    }, [
        isEdit,
        loadCreditCode,
    ]);

    const handleSubmit =
        useCallback(
            async (values) => {
                try {
                    setSaving(true);
                    setError('');

                    const code =
                        normalizeCode(
                            values.code,
                        );

                    const email =
                        normalizeEmail(
                            values.validEmail,
                        );

                    const currency =
                        String(
                            values.currency ||
                            '',
                        )
                            .trim()
                            .toUpperCase();

                    const discount =
                        String(
                            values.discount ||
                            '',
                        ).trim();

                    const scope =
                        String(
                            values.redeemScope ||
                            '',
                        ).trim();

                    const shopId =
                        normalizeShopId(
                            values.redeemShopId,
                        );

                    if (!code) {
                        setError(
                            'Code is required.',
                        );
                        return;
                    }

                    if (
                        !isEdit &&
                        code.length > 10
                    ) {
                        setError(
                            'Code must be 10 characters or less.',
                        );
                        return;
                    }

                    if (!email) {
                        setError(
                            'Email is required.',
                        );
                        return;
                    }

                    if (
                        !currency ||
                        currency.length !==
                        3
                    ) {
                        setError(
                            'Currency must be 3 letters (e.g. EUR).',
                        );
                        return;
                    }

                    if (
                        !isValidMoney(
                            discount,
                        )
                    ) {
                        setError(
                            'Discount must be a number with up to 2 decimals (e.g. "10.50").',
                        );
                        return;
                    }

                    const startIso =
                        fromDateTimeLocal(
                            values.validStartDate,
                        );

                    const endIso =
                        fromDateTimeLocal(
                            values.validEndDate,
                        );

                    if (
                        !startIso ||
                        !endIso
                    ) {
                        setError(
                            'Valid start/end date is required.',
                        );
                        return;
                    }

                    if (
                        new Date(
                            endIso,
                        ).getTime() <
                        new Date(
                            startIso,
                        ).getTime()
                    ) {
                        setError(
                            'Valid end date must be later than or equal to start date.',
                        );
                        return;
                    }

                    if (
                        scope ===
                        'SAME_SHOP' &&
                        !shopId
                    ) {
                        setError(
                            'Redeem Shop ID is required when scope is "Same shop only".',
                        );
                        return;
                    }

                    const payload = {
                        ...(isEdit
                            ? {}
                            : {
                                code,
                            }),

                        discount,
                        currency,

                        validEmail:
                            email,

                        validStartDate:
                            startIso,

                        validEndDate:
                            endIso,

                        isActive:
                            !!values.isActive,

                        redeemScope:
                            scope,

                        redeemShopId:
                            scope ===
                                'ANY_SHOP'
                                ? null
                                : shopId ||
                                null,
                    };

                    if (isEdit) {
                        const {
                            data,
                        } =
                            await API.patch(
                                `/codes/credits/${id}`,
                                payload,
                            );

                        alert(
                            'Credit code updated successfully!',
                        );

                        setInitialValues(
                            (prev) => ({
                                ...prev,
                                ...data,

                                discount:
                                    data?.discount !=
                                        null
                                        ? String(
                                            data.discount,
                                        )
                                        : prev.discount,

                                currency:
                                    (
                                        data?.currency ??
                                        prev.currency
                                    ).toUpperCase(),

                                validStartDate:
                                    toDateTimeLocal(
                                        data?.validStartDate ??
                                        startIso,
                                    ),

                                validEndDate:
                                    toDateTimeLocal(
                                        data?.validEndDate ??
                                        endIso,
                                    ),

                                redeemShopId:
                                    data?.redeemShopId ??
                                    '',

                                notifiedAt:
                                    data?.notifiedAt ??
                                    prev.notifiedAt,

                                isUsed:
                                    !!(
                                        data?.isUsed ??
                                        prev.isUsed
                                    ),

                                isExpired:
                                    !!(
                                        data?.isExpired ??
                                        prev.isExpired
                                    ),

                                createdAt:
                                    data?.createdAt ??
                                    prev.createdAt,

                                updatedAt:
                                    data?.updatedAt ??
                                    prev.updatedAt,

                                orderId:
                                    data?.orderId ??
                                    prev.orderId,

                                notificationOutboxId:
                                    data?.notificationOutboxId ??
                                    prev.notificationOutboxId,
                            }),
                        );

                        return;
                    }

                    const {
                        data,
                    } =
                        await API.post(
                            '/codes/credits',
                            payload,
                        );

                    alert(
                        'Credit code created successfully!',
                    );

                    if (data?.id) {
                        navigate(
                            `/codes/credits/${data.id}`,
                        );
                    } else {
                        navigate(
                            '/codes/credits',
                        );
                    }
                } catch (e) {
                    setError(
                        e?.response?.data
                            ?.message ||
                        e?.message ||
                        'Save failed',
                    );
                } finally {
                    setSaving(false);
                }
            },
            [
                id,
                isEdit,
                navigate,
            ],
        );

    return (
        <Formik
            initialValues={
                initialValues
            }
            enableReinitialize
            onSubmit={
                handleSubmit
            }
        >
            {({
                handleSubmit:
                formikHandleSubmit,
                values,
                setFieldValue,
            }) => {
                const scope =
                    String(
                        values.redeemScope ||
                        'ANY_SHOP',
                    );

                const lockCode =
                    isEdit;

                const shopDisabled =
                    scope ===
                    'ANY_SHOP';

                const notified =
                    !!values.notifiedAt;

                const active =
                    !!values.isActive;

                const used =
                    !!values.isUsed;

                const expired =
                    !!values.isExpired;

                return (
                    <Container
                        fluid
                        className="d-flex flex-column"
                    >
                        <Row className="mb-4">
                            <Col sm={8}>
                                <span className="fw-semibold">
                                    {isEdit
                                        ? 'Credit Code Details'
                                        : 'Create Credit Code'}
                                </span>
                            </Col>

                            <Col
                                sm={4}
                                className="text-end"
                            >
                                <span className="fw-semibold">
                                    <NavLink
                                        to="/dashboard"
                                        className="block-link"
                                    >
                                        Dashboard
                                    </NavLink>{' '}
                                    -{' '}
                                    <NavLink
                                        to="/codes/credits"
                                        className="block-link"
                                    >
                                        Credit
                                        Codes
                                    </NavLink>{' '}
                                    -{' '}
                                    {isEdit
                                        ? 'Details'
                                        : 'New'}
                                </span>
                            </Col>
                        </Row>

                        <Row>
                            <Col sm={12}>
                                <Card className="h-100 p-2">
                                    <Card.Body>
                                        {loading ? (
                                            <div className="d-flex align-items-center gap-2 text-muted">
                                                <Spinner
                                                    animation="border"
                                                    size="sm"
                                                />

                                                <span>
                                                    Loading…
                                                </span>
                                            </div>
                                        ) : (
                                            <Row>
                                                <Col
                                                    sm={
                                                        12
                                                    }
                                                    md={
                                                        6
                                                    }
                                                >
                                                    <FormSection
                                                        title="Main Information"
                                                        level={
                                                            2
                                                        }
                                                    >
                                                        <Form.Group className="mb-3">
                                                            <Form.Label className="fw-semibold mb-2">
                                                                Code{' '}
                                                                <span className="text-danger">
                                                                    *
                                                                </span>
                                                            </Form.Label>

                                                            <InputGroup>
                                                                <Form.Control
                                                                    type="text"
                                                                    value={
                                                                        values.code
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) =>
                                                                        setFieldValue(
                                                                            'code',
                                                                            e
                                                                                .target
                                                                                .value,
                                                                        )
                                                                    }
                                                                    placeholder="e.g. CREDIT10"
                                                                    disabled={
                                                                        lockCode
                                                                    }
                                                                />

                                                                <Button
                                                                    variant="outline-secondary"
                                                                    type="button"
                                                                    onClick={() =>
                                                                        copyToClipboard(
                                                                            values.code,
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        !values.code
                                                                    }
                                                                    title="Copy"
                                                                >
                                                                    <FaRegCopy />
                                                                </Button>
                                                            </InputGroup>

                                                            <Form.Text className="text-muted">
                                                                {lockCode
                                                                    ? 'Code is immutable after creation.'
                                                                    : 'Max 10 characters. Recommend uppercase letters + numbers.'}
                                                            </Form.Text>
                                                        </Form.Group>

                                                        <Form.Group className="mb-3">
                                                            <Form.Label className="fw-semibold mb-2">
                                                                Valid
                                                                Email{' '}
                                                                <span className="text-danger">
                                                                    *
                                                                </span>
                                                            </Form.Label>

                                                            <Form.Control
                                                                type="email"
                                                                value={
                                                                    values.validEmail
                                                                }
                                                                onChange={(
                                                                    e,
                                                                ) =>
                                                                    setFieldValue(
                                                                        'validEmail',
                                                                        e
                                                                            .target
                                                                            .value,
                                                                    )
                                                                }
                                                                placeholder="customer@example.com"
                                                            />

                                                            <Form.Text className="text-muted">
                                                                Only
                                                                this
                                                                email
                                                                can
                                                                redeem
                                                                the
                                                                credit
                                                                code.
                                                            </Form.Text>
                                                        </Form.Group>

                                                        <Row className="g-3">
                                                            <Col
                                                                md={
                                                                    6
                                                                }
                                                            >
                                                                <Form.Group className="mb-3">
                                                                    <Form.Label className="fw-semibold mb-2">
                                                                        Discount{' '}
                                                                        <span className="text-danger">
                                                                            *
                                                                        </span>
                                                                    </Form.Label>

                                                                    <Form.Control
                                                                        type="text"
                                                                        inputMode="decimal"
                                                                        value={
                                                                            values.discount
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) =>
                                                                            setFieldValue(
                                                                                'discount',
                                                                                e
                                                                                    .target
                                                                                    .value,
                                                                            )
                                                                        }
                                                                        placeholder="e.g. 10.50"
                                                                    />

                                                                    <Form.Text className="text-muted">
                                                                        Up
                                                                        to
                                                                        2
                                                                        decimals.
                                                                    </Form.Text>
                                                                </Form.Group>
                                                            </Col>

                                                            <Col
                                                                md={
                                                                    6
                                                                }
                                                            >
                                                                <Form.Group className="mb-3">
                                                                    <Form.Label className="fw-semibold mb-2">
                                                                        Currency{' '}
                                                                        <span className="text-danger">
                                                                            *
                                                                        </span>
                                                                    </Form.Label>

                                                                    <Form.Control
                                                                        type="text"
                                                                        value={
                                                                            values.currency
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) =>
                                                                            setFieldValue(
                                                                                'currency',
                                                                                e.target.value.toUpperCase(),
                                                                            )
                                                                        }
                                                                        placeholder="EUR"
                                                                        maxLength={
                                                                            3
                                                                        }
                                                                    />

                                                                    <Form.Text className="text-muted">
                                                                        3-letter
                                                                        code,
                                                                        e.g.
                                                                        EUR.
                                                                    </Form.Text>
                                                                </Form.Group>
                                                            </Col>
                                                        </Row>

                                                        <Row className="g-3">
                                                            <Col
                                                                md={
                                                                    6
                                                                }
                                                            >
                                                                <Form.Group className="mb-3">
                                                                    <Form.Label className="fw-semibold mb-2">
                                                                        Valid
                                                                        Start{' '}
                                                                        <span className="text-danger">
                                                                            *
                                                                        </span>
                                                                    </Form.Label>

                                                                    <Form.Control
                                                                        type="datetime-local"
                                                                        value={
                                                                            values.validStartDate
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) =>
                                                                            setFieldValue(
                                                                                'validStartDate',
                                                                                e
                                                                                    .target
                                                                                    .value,
                                                                            )
                                                                        }
                                                                    />
                                                                </Form.Group>
                                                            </Col>

                                                            <Col
                                                                md={
                                                                    6
                                                                }
                                                            >
                                                                <Form.Group className="mb-3">
                                                                    <Form.Label className="fw-semibold mb-2">
                                                                        Valid
                                                                        End{' '}
                                                                        <span className="text-danger">
                                                                            *
                                                                        </span>
                                                                    </Form.Label>

                                                                    <Form.Control
                                                                        type="datetime-local"
                                                                        value={
                                                                            values.validEndDate
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) =>
                                                                            setFieldValue(
                                                                                'validEndDate',
                                                                                e
                                                                                    .target
                                                                                    .value,
                                                                            )
                                                                        }
                                                                    />
                                                                </Form.Group>
                                                            </Col>
                                                        </Row>

                                                        <Row className="g-3">
                                                            <Col
                                                                md={
                                                                    6
                                                                }
                                                            >
                                                                <Form.Group className="mb-3">
                                                                    <Form.Label className="fw-semibold mb-2">
                                                                        Redeem
                                                                        Scope
                                                                    </Form.Label>

                                                                    <Form.Select
                                                                        value={
                                                                            values.redeemScope
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) => {
                                                                            const next =
                                                                                e
                                                                                    .target
                                                                                    .value;

                                                                            setFieldValue(
                                                                                'redeemScope',
                                                                                next,
                                                                            );

                                                                            if (
                                                                                next ===
                                                                                'ANY_SHOP'
                                                                            ) {
                                                                                setFieldValue(
                                                                                    'redeemShopId',
                                                                                    '',
                                                                                );
                                                                            }
                                                                        }}
                                                                    >
                                                                        {REDEEM_SCOPE_OPTIONS.map(
                                                                            (
                                                                                option,
                                                                            ) => (
                                                                                <option
                                                                                    key={
                                                                                        option.value
                                                                                    }
                                                                                    value={
                                                                                        option.value
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        option.label
                                                                                    }
                                                                                </option>
                                                                            ),
                                                                        )}
                                                                    </Form.Select>

                                                                    <Form.Text className="text-muted">
                                                                        {scope ===
                                                                            'ANY_SHOP'
                                                                            ? 'Can be used at any shop.'
                                                                            : 'Can only be used at the specified shop.'}
                                                                    </Form.Text>
                                                                </Form.Group>
                                                            </Col>

                                                            <Col
                                                                md={
                                                                    6
                                                                }
                                                            >
                                                                <Form.Group className="mb-3">
                                                                    <Form.Label className="fw-semibold mb-2">
                                                                        Redeem
                                                                        Shop
                                                                        ID{' '}
                                                                        {scope ===
                                                                            'SAME_SHOP' ? (
                                                                            <span className="text-danger">
                                                                                *
                                                                            </span>
                                                                        ) : null}
                                                                    </Form.Label>

                                                                    <Form.Control
                                                                        type="text"
                                                                        value={
                                                                            values.redeemShopId ||
                                                                            ''
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) =>
                                                                            setFieldValue(
                                                                                'redeemShopId',
                                                                                e
                                                                                    .target
                                                                                    .value,
                                                                            )
                                                                        }
                                                                        placeholder={
                                                                            shopDisabled
                                                                                ? 'Not required'
                                                                                : 'UUID of shop'
                                                                        }
                                                                        disabled={
                                                                            shopDisabled
                                                                        }
                                                                    />

                                                                    <Form.Text className="text-muted">
                                                                        {scope ===
                                                                            'SAME_SHOP'
                                                                            ? 'Required when scope is “Same shop only”.'
                                                                            : 'Auto-cleared when scope is “Any shop”.'}
                                                                    </Form.Text>
                                                                </Form.Group>
                                                            </Col>
                                                        </Row>

                                                        <Form.Group className="mb-3">
                                                            <Form.Check
                                                                type="switch"
                                                                id="isActive"
                                                                label="Active"
                                                                checked={
                                                                    !!values.isActive
                                                                }
                                                                onChange={(
                                                                    e,
                                                                ) =>
                                                                    setFieldValue(
                                                                        'isActive',
                                                                        e
                                                                            .target
                                                                            .checked,
                                                                    )
                                                                }
                                                            />
                                                        </Form.Group>

                                                        {error ? (
                                                            <div className="text-danger mt-2">
                                                                {
                                                                    error
                                                                }
                                                            </div>
                                                        ) : null}
                                                    </FormSection>
                                                </Col>

                                                <Col
                                                    sm={
                                                        12
                                                    }
                                                    md={
                                                        6
                                                    }
                                                >
                                                    <FormSection
                                                        title="Status & Audit"
                                                        level={
                                                            2
                                                        }
                                                    >
                                                        <Card className="p-3">
                                                            <div className="d-flex justify-content-between align-items-start">
                                                                <div>
                                                                    <div className="fw-semibold">
                                                                        Status
                                                                    </div>

                                                                    <div className="text-muted small">
                                                                        Read-only
                                                                        information.
                                                                    </div>
                                                                </div>

                                                                <div className="d-flex gap-1 flex-wrap justify-content-end">
                                                                    <Badge
                                                                        bg={
                                                                            active
                                                                                ? 'success'
                                                                                : 'secondary'
                                                                        }
                                                                    >
                                                                        {active
                                                                            ? 'Active'
                                                                            : 'Inactive'}
                                                                    </Badge>

                                                                    <Badge
                                                                        bg={
                                                                            used
                                                                                ? 'primary'
                                                                                : 'light'
                                                                        }
                                                                        text={
                                                                            used
                                                                                ? undefined
                                                                                : 'dark'
                                                                        }
                                                                    >
                                                                        {used
                                                                            ? 'Used'
                                                                            : 'Unused'}
                                                                    </Badge>

                                                                    {expired ? (
                                                                        <Badge bg="danger">
                                                                            Expired
                                                                        </Badge>
                                                                    ) : (
                                                                        <Badge
                                                                            bg="light"
                                                                            text="dark"
                                                                        >
                                                                            Valid
                                                                        </Badge>
                                                                    )}

                                                                    <Badge
                                                                        bg={
                                                                            notified
                                                                                ? 'info'
                                                                                : 'light'
                                                                        }
                                                                        text={
                                                                            notified
                                                                                ? undefined
                                                                                : 'dark'
                                                                        }
                                                                    >
                                                                        {notified
                                                                            ? 'Notified'
                                                                            : 'Not sent'}
                                                                    </Badge>
                                                                </div>
                                                            </div>

                                                            <hr />

                                                            <Row className="g-3">
                                                                <Col
                                                                    md={
                                                                        6
                                                                    }
                                                                >
                                                                    <div className="text-muted small">
                                                                        Order
                                                                        ID
                                                                    </div>

                                                                    <div className="fw-semibold">
                                                                        {values.orderId ? (
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={
                                                                                    <Tooltip>
                                                                                        {
                                                                                            values.orderId
                                                                                        }
                                                                                    </Tooltip>
                                                                                }
                                                                            >
                                                                                <span
                                                                                    style={{
                                                                                        cursor: 'help',
                                                                                    }}
                                                                                >
                                                                                    {String(
                                                                                        values.orderId,
                                                                                    ).slice(
                                                                                        0,
                                                                                        8,
                                                                                    )}
                                                                                    …
                                                                                </span>
                                                                            </OverlayTrigger>
                                                                        ) : (
                                                                            '—'
                                                                        )}
                                                                    </div>
                                                                </Col>

                                                                <Col
                                                                    md={
                                                                        6
                                                                    }
                                                                >
                                                                    <div className="text-muted small">
                                                                        Outbox
                                                                        ID
                                                                    </div>

                                                                    <div className="fw-semibold">
                                                                        {values.notificationOutboxId ? (
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={
                                                                                    <Tooltip>
                                                                                        {
                                                                                            values.notificationOutboxId
                                                                                        }
                                                                                    </Tooltip>
                                                                                }
                                                                            >
                                                                                <span
                                                                                    style={{
                                                                                        cursor: 'help',
                                                                                    }}
                                                                                >
                                                                                    {String(
                                                                                        values.notificationOutboxId,
                                                                                    ).slice(
                                                                                        0,
                                                                                        8,
                                                                                    )}
                                                                                    …
                                                                                </span>
                                                                            </OverlayTrigger>
                                                                        ) : (
                                                                            '—'
                                                                        )}
                                                                    </div>
                                                                </Col>
                                                            </Row>

                                                            <hr />

                                                            <Row className="g-3">
                                                                <Col
                                                                    md={
                                                                        6
                                                                    }
                                                                >
                                                                    <div className="text-muted small">
                                                                        Created
                                                                    </div>

                                                                    <div className="fw-semibold">
                                                                        {values.createdAt
                                                                            ? baseFormatters.date.numeric.format(
                                                                                new Date(
                                                                                    values.createdAt,
                                                                                ),
                                                                            )
                                                                            : '—'}
                                                                    </div>
                                                                </Col>

                                                                <Col
                                                                    md={
                                                                        6
                                                                    }
                                                                >
                                                                    <div className="text-muted small">
                                                                        Updated
                                                                    </div>

                                                                    <div className="fw-semibold">
                                                                        {values.updatedAt
                                                                            ? baseFormatters.date.numeric.format(
                                                                                new Date(
                                                                                    values.updatedAt,
                                                                                ),
                                                                            )
                                                                            : '—'}
                                                                    </div>
                                                                </Col>
                                                            </Row>

                                                            <Row className="g-3 mt-1">
                                                                <Col
                                                                    md={
                                                                        12
                                                                    }
                                                                >
                                                                    <div className="text-muted small">
                                                                        Notified
                                                                        At
                                                                    </div>

                                                                    <div className="fw-semibold">
                                                                        {values.notifiedAt
                                                                            ? baseFormatters.date.numeric.format(
                                                                                new Date(
                                                                                    values.notifiedAt,
                                                                                ),
                                                                            )
                                                                            : '—'}
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </Card>
                                                    </FormSection>

                                                    <FormSection
                                                        title="Notes"
                                                        level={
                                                            2
                                                        }
                                                    >
                                                        <div className="text-muted">
                                                            <ul className="mb-0">
                                                                <li>
                                                                    Use{' '}
                                                                    <span className="fw-semibold">
                                                                        Active
                                                                    </span>{' '}
                                                                    to
                                                                    stop
                                                                    new
                                                                    redemptions.
                                                                </li>

                                                                <li>
                                                                    <span className="fw-semibold">
                                                                        Any
                                                                        shop
                                                                    </span>{' '}
                                                                    will
                                                                    clear
                                                                    Redeem
                                                                    Shop
                                                                    ID
                                                                    automatically.
                                                                </li>

                                                                <li>
                                                                    If
                                                                    email
                                                                    notification
                                                                    is
                                                                    sent,{' '}
                                                                    <span className="fw-semibold">
                                                                        Notified
                                                                    </span>{' '}
                                                                    will
                                                                    show
                                                                    the
                                                                    timestamp.
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </FormSection>
                                                </Col>

                                                <Col
                                                    sm={
                                                        12
                                                    }
                                                    className="gap-3 mt-2 text-end"
                                                >
                                                    <Button
                                                        type="button"
                                                        variant="secondary"
                                                        className="me-2"
                                                        onClick={() =>
                                                            navigate(
                                                                '/codes/credits',
                                                            )
                                                        }
                                                        disabled={
                                                            saving
                                                        }
                                                    >
                                                        Back
                                                    </Button>

                                                    <Button
                                                        type="button"
                                                        onClick={() =>
                                                            formikHandleSubmit()
                                                        }
                                                        className="border ms-1 btn-custom-success"
                                                        disabled={
                                                            saving
                                                        }
                                                    >
                                                        {saving ? (
                                                            <>
                                                                <Spinner
                                                                    animation="border"
                                                                    size="sm"
                                                                    className="me-2"
                                                                />

                                                                Saving…
                                                            </>
                                                        ) : (
                                                            'Save'
                                                        )}
                                                    </Button>
                                                </Col>
                                            </Row>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                );
            }}
        </Formik>
    );
};

export default CreditCodeFormPage;